import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {IVideo, IVideoGenerationStatus} from "../../../../../sidebar/sidebar.component";
import {NewProject} from "../../../../../../../interfaces/app.interfaces";
import {Model} from "../../../../../../../services/api.service";
import {ConfirmationService, MenuItem, MessageService} from "primeng/api";
import {AppStateService} from "../../../../../../../services/app.state.service";
import {EventBusService, SpinnerLocation} from "../../../../../../../services/event-bus.service";
import {SpinnerPosition} from "../../../../../../../shared/spinner/spinner.component";
import {VideoCollectionService} from "../../../../../../../services/video-collection.service";
import {finalize, interval, Subject, switchMap, takeUntil, takeWhile, tap, throwError} from "rxjs";
import {catchError} from "rxjs/operators";

@Component({
  selector: 'app-video',
  providers: [ConfirmationService],
  templateUrl: './video.component.html',
  styleUrl: './video.component.scss'
})
export class VideoComponent implements OnInit, OnChanges, OnDestroy {

  @Input() video!: IVideo;
  @Input() videoCollection!: NewProject;
  @Input() isFirst!: boolean;

  @Output() onCreate: EventEmitter<CreateVideoData> = new EventEmitter<CreateVideoData>();
  @Output() onDelete: EventEmitter<DeleteVideoData> = new EventEmitter<DeleteVideoData>();

  previewShown = false;
  videoPreview!: string;
  failed =  false;
  isCopying = false;
  isLoading = false;
  videoModel?: Model;
  videoBlob!: Blob;

  private destroy$ = new Subject<void>();

  protected SpinnerPosition = SpinnerPosition;

  menuItems: MenuItem[] = [
    {
      icon: 'pi pi-copy',
      label: 'Copy prompt',
      command: () => {
        this.copyPrompt();
      }
    },
    {
      icon: 'pi pi-code',
      label: 'Use prompt',
      command: () => {
        this.usePrompt();
      }
    },
    {
      icon: 'pi pi-download',
      label: 'Download',
      command: () => {
        this.downloadVideo();
      }
    },
    {
      icon: 'pi pi-trash',
      label: 'Delete',
      command: () => {
        this.confirmDeleteVideo();
      }
    },
  ];

  constructor(
      private stateService: AppStateService,
      private videoService: VideoCollectionService,
      private eventBus: EventBusService,
      private confirmationService: ConfirmationService,
      private messageService: MessageService,
  ) {}

  isVideoLoading(event: boolean): void {
    this.isLoading = event;
  }

  videoPreviewUrl(event: string): void {
    this.videoPreview = event;
  }

  getBackgroundClass() {
    return `background-image: url(${this.videoPreview})`;
  }

  getVideoBlob(video: Blob): void {
    this.videoBlob = video;
  }

  submitVideoCreation(message: string):void {
    this.isLoading = true;
    this.videoService.submitVideoCreation(this.videoCollection.id, message, this.stateService.selectedAIModel.value).pipe(
        catchError((error)=> {
          return throwError(() => {
            this.failed = true;
            return new Error('Failed to generate image');
          })
        }),
        finalize(() => {
          // this.isLoading = false;
        })
    ).subscribe(videos => {
      const newVideo = {...videos[0], existing: true};
      this.onCreate.emit({
        video: newVideo,
        tempId: this.video.tempId
      });
      this.video = newVideo;
      this.videoModel = newVideo.model;

      if (this.isFirst) {
        this.eventBus.updateVideoCollectionNameById.send(this.videoCollection.id);
      }

      // Update video status
      this.updateVideoData();

    },() => {
      this.onDelete.emit({
        video: this.video
      });
    })
  }

  copyPrompt() {
    navigator.clipboard.writeText(this.video.prompt).then();
    this.isCopying = true;
    setTimeout(()=> {
      this.isCopying = false;
    }, 2000);
  }

  downloadVideo() {
    const a = document.createElement('a');
    const url = URL.createObjectURL(this.videoBlob);
    a.href = url;
    a.download = 'video.mp4';
    a.click();
    URL.revokeObjectURL(url);
  }

  usePrompt() {
    this.eventBus.newPrompt.next(this.video.prompt);
  }

  confirmDeleteVideo(): void {
    this.confirmationService.confirm({
      key: 'video-item',
      message: 'Are you sure you want to delete this video?',
      accept: () => {
        this.deleteVideo();
      }
    });
  }

  deleteVideo() {
    this.videoService.deleteVideo(this.videoCollection.id, this.video.id).pipe(
        tap(() => {
          this.eventBus.setIsLoading(true, SpinnerLocation.Body)
        }),
        finalize(()=> {
          this.eventBus.setIsLoading(false, SpinnerLocation.Body)
        })
    ).subscribe(() => {
      this.onDelete.emit({
        video: this.video
      });
    })
  }

  updateVideoData(): void {
    this.isLoading = true;
    interval(5000)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.videoService.getVideoById(this.video.id, this.videoCollection.id)),
        tap(response => {

        }),
        takeWhile(response => response.generationStatus === IVideoGenerationStatus.InProgress, true)
      )
      .subscribe(response => {
        if (response) {
          if (response.generationStatus === IVideoGenerationStatus.CompletedWithError) {
            this.onDelete.emit({
              video: this.video
            });
            this.messageService.add({
              severity: 'error',
              summary: 'Warning',
              detail: 'Generation has been completed with error',
              key: 'errors',
              life: 5000,
              sticky: false,
              closable: true
            })
          }
          if (response.generationStatus === IVideoGenerationStatus.CompletedSuccessfully) {
            this.video = {
              ...this.video,
              generationStatus: response.generationStatus
            }
          }
        }
      })
  }

  ngOnChanges(changes: SimpleChanges): void {
    const video = changes['video'];
    if ((video && !video.firstChange)) {
      if (this.video.existing) {
        this.videoModel = this.video.model;
      } else {
        this.submitVideoCreation(this.video.prompt);
      }
    }
  }

  ngOnInit(): void {
    if (this.video.existing) {
      this.videoModel = this.video.model;
      if (this.video.generationStatus !== IVideoGenerationStatus.CompletedSuccessfully) {
        this.updateVideoData();
      }
    } else {
      this.submitVideoCreation(this.video.prompt);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}

export interface CreateVideoData {
  video: IVideo;
  tempId: string;
}
export interface DeleteVideoData {
  video: IVideo;
}
